import { PatientService } from 'src/services/patient.service'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { TPatient } from 'src/types/patient'
import { useNotification } from 'src/providers/notification.provider'
import { CurecallConfirmationModal, ECurecallNotificationAppearance } from '@honestica/curecall-ui'
import { Trans, useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { getFullName } from 'src/utils/string-formatter.util'
import { TPlanningPatientMedEvent } from 'src/pages/medical/planning/_components/planning.type'
import { usePatientList } from 'src/pages/medical/patients/_components/patients.provider'

type Props = {
  patient: Pick<TPatient, 'id' | 'firstname' | 'lastname' | 'isArchived'>
  isModalOpen: boolean
  onToggle: (event?: React.MouseEvent<HTMLElement>) => void
}

const ArchiveAccountModal = ({ patient, isModalOpen, onToggle }: Props) => {
  const { project } = useCurrentProject()
  const { notify } = useNotification()
  const { updatePatientList } = usePatientList()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const shouldArchivePatient = !patient?.isArchived

  const handleArchiveAccount = async () => {
    try {
      const updatedPatient = await PatientService.update(project.id, patient.id, {
        isArchived: shouldArchivePatient,
      })
      updatePatientList({ method: 'PUT', data: { patient: updatedPatient } })
      queryClient.setQueryData<TPatient>(['patients', project.id, patient.id], (oldData) => {
        return oldData ? { ...oldData, ...updatedPatient } : updatedPatient
      })
      // Update planning queries: remove archived patient from all planning tabs
      queryClient.getQueriesData({ queryKey: ['planning-patient-med-events'] }).forEach(([queryKey]) => {
        queryClient.setQueryData(queryKey, (oldData: any) => {
          if (!oldData) return oldData
          const patientMedEventsUpdated = oldData.patientMedEvents.filter(
            (planningPatientMedEvent: TPlanningPatientMedEvent) =>
              planningPatientMedEvent.Patient.id !== updatedPatient.id,
          )
          return { ...oldData, patientMedEvents: patientMedEventsUpdated }
        })
      })
      if (shouldArchivePatient) {
        localStorage.removeItem('humanReviewSelectedPatientMedEventId')
        queryClient.removeQueries({ queryKey: ['human-review-patient-comments', patient.id] })
        queryClient.removeQueries({ queryKey: ['human-review-requests-detail', project.id] })
        queryClient.removeQueries({ queryKey: ['human-review-requests-tabs', project.id] })
        queryClient.removeQueries({ queryKey: ['human-review-requests-list', project.id] })
      }

      notify({
        title: t('common.actions.success'),
        message: shouldArchivePatient
          ? t('components.patient.archive-or-delete.archive-notification-success')
          : t('components.patient.archive-or-delete.unarchive-notification-success'),
        appearance: ECurecallNotificationAppearance.SUCCESS,
      })
    } catch {
      notify({
        title: t('common.actions.error'),
        message: shouldArchivePatient
          ? t('components.patient.archive-or-delete.archive-notification-error')
          : t('components.patient.archive-or-delete.unarchive-notification-error'),
        appearance: ECurecallNotificationAppearance.ERROR,
      })
    } finally {
      onToggle()
    }
  }

  return (
    <CurecallConfirmationModal
      isOpen={isModalOpen}
      color="ALERT"
      onConfirm={handleArchiveAccount}
      onCancel={() => onToggle()}
      title={
        shouldArchivePatient
          ? t('components.patient.archive-or-delete.confirmation-modal-archive-title')
          : t('components.patient.archive-or-delete.confirmation-modal-unarchive-title')
      }
      cancelButtonLabel={t('common.actions.cancel')}
      confirmButtonLabel={
        shouldArchivePatient
          ? t('components.patient.archive-or-delete.archive')
          : t('components.patient.archive-or-delete.unarchive')
      }
    >
      <div className="c-flex c-flex-col c-gap-4 c-items-start">
        {shouldArchivePatient ? ArchiveModalContent() : UnarchiveModalContent(patient)}
      </div>
    </CurecallConfirmationModal>
  )
}

const ArchiveModalContent = () => {
  const { t } = useTranslation()
  return (
    <>
      <section className="[&>p]:c-m-0 c-flex c-flex-col c-gap-2">
        <p>
          <Trans
            i18nKey="components.patient.archive-or-delete.confirmation-modal-archive-description-1"
            components={{ important: <b /> }}
          />
        </p>
        <ul className="c-list-disc [&>*]:c-ml-8">
          <li>
            <Trans
              i18nKey="components.patient.archive-or-delete.confirmation-modal-archive-description-item-1"
              components={{ important: <b /> }}
            />
          </li>
          <li>
            <Trans
              i18nKey="components.patient.archive-or-delete.confirmation-modal-archive-description-item-2"
              components={{ important: <b /> }}
            />
          </li>
        </ul>
        <p>{t('components.patient.archive-or-delete.confirmation-modal-archive-description-2')}</p>
        <p>
          <Trans
            i18nKey="components.patient.archive-or-delete.confirmation-modal-archive-description-3"
            components={{ important: <b /> }}
          />
        </p>
      </section>
      <p className="c-m-0">
        {t('components.patient.archive-or-delete.confirmation-modal-archive-description-4')}
      </p>
    </>
  )
}

const UnarchiveModalContent = (patient: Pick<TPatient, 'firstname' | 'lastname'>) => {
  const { t } = useTranslation()
  return (
    <>
      <span>{t('components.patient.archive-or-delete.confirmation-modal-unarchive-description')}</span>
      <span className="c-mx-auto c-font-semibold c-text-lg">{getFullName(patient)}</span>
    </>
  )
}

export default ArchiveAccountModal
